/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A dotted path reaches data, never the language underneath it** (`#1118` item 3).
 *
 * An authored path — a mapping, a decorator's `sourcePath`, a response mapping's source — was walked
 * with `current[part]`, so it reached anything JavaScript puts on a value: `genres.length` answered
 * `2`, `content.constructor.name` answered `"Object"`, and both went out on the wire. Nobody designed
 * that vocabulary, and a path vocabulary that reaches object internals is a surface we cannot say
 * anything true about.
 *
 * So a segment resolves only where it names an **own property of an object**, and on an array only
 * where it is an **index** — `genres.0` keeps working, and `length`, which an array carries as an own
 * property, does not. A primitive has no segments beneath it — a string's `length` is the language's,
 * not the publisher's.
 *
 * Every walk of an authored path uses this. Three were written out privately and identically, which is
 * *one helper, every path* — and a fourth copy with the guard missing would be the defect back.
 */
export function readOwnPath(target: unknown, parts: readonly string[]): unknown {
  let current: unknown = target;
  for (const raw of parts) {
    if (current === null || typeof current !== 'object') return undefined;
    let part = raw;
    if (Array.isArray(current)) {
      // `last` is the array's final member — the same word, with the same meaning, as the ORTB body's
      // `$at.last` (RS4b). It is resolved here rather than per caller so the vocabulary is one.
      if (part === LAST_POSITION) {
        if (current.length === 0) return undefined;
        part = String(current.length - 1);
      } else if (!/^\d+$/.test(part)) return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(current, part)) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * **The one word for an array's final member**, on a read path and on an ORTB body pattern alike
 * (`DEMAND_REQUEST_SHAPE_DESIGN.md` RS4b): the highest index present at the moment it is resolved.
 */
export const LAST_POSITION = 'last';

/**
 * **Split an authored path into its segments, accepting `[n]` for `.n`** (`#1118` item 1).
 *
 * `session.content.genres.0` always resolved; `session.content.genres[0]` — the spelling anyone would
 * type — did not, because the walk split on dots only and then looked for a key called `genres[0]`.
 * Both spellings now produce the same segments, so every walker that splits through here accepts
 * both, and none of them has to know the bracket form exists. `[last]` is `.last`.
 *
 * Only a bracket holding an index or `last` is rewritten; anything else in brackets is left as part
 * of its segment and will simply not resolve, which is what it did before.
 */
export function splitAuthoredPath(path: string): string[] {
  return path
    .replace(/\[(\d+|last)\]/g, '.$1')
    .split('.')
    .filter((part) => part !== '');
}

/**
 * **The word for *every* member of a list** — `[*]`, the same word, with the same meaning, as the ORTB
 * body's `imp[*]` (`DEMAND_REQUEST_SHAPE_DESIGN.md` RS4c), here for a calculation's operand (`CO8`,
 * slice `2e`, `#1369`).
 *
 * `pod.cachedBidRefs[*].bid` is *the `bid` of each cached bid*, stored as a collection operand's two
 * paths: the list, and the member read on each. Where `[n]` and `[last]` name **one** member and are
 * split by {@link splitAuthoredPath}, this names all of them — which is not a position a walk can
 * stand on, so it is split here and nowhere else. **`splitAuthoredPath` deliberately does not learn
 * it**: a map with a literal `'*'` key exists (`roundParamOverrides`), and a split `[*]` would start
 * resolving to that bucket on a path that has always resolved to nothing.
 */
export const EACH_POSITION = '[*]';

/**
 * `pod.cachedBidRefs[*].bid` → `{ path: 'pod.cachedBidRefs', member: 'bid' }`; `session.content.genres[*]`
 * → `{ path: 'session.content.genres' }`. `undefined` where the text names no list at all. Only the
 * first `[*]` splits: a list of lists is not a collection this operand folds, so a second one stays in
 * the member path, where it resolves to nothing and the path check says so.
 */
export function splitAtEachPosition(text: string): { path: string; member?: string } | undefined {
  const at = text.indexOf(EACH_POSITION);
  if (at <= 0) return undefined;
  const path = text.slice(0, at);
  const member = text.slice(at + EACH_POSITION.length).replace(/^\./, '');
  return member ? { path, member } : { path };
}

/** The inverse of {@link splitAtEachPosition} — how a collection operand is written back as text. */
export function joinAtEachPosition(operand: { path: string; member?: string }): string {
  return `${operand.path}${EACH_POSITION}${operand.member ? `.${operand.member}` : ''}`;
}
